import { UserFacingError } from "./errors";
import { compareOriginalView } from "./original-view";
import type { RedlineAttemptRecorder } from "./redline-apply-recorder";
import type { RedlineApplyContext } from "./redline-snapshot";

const TOAST_EXCERPT_LIMIT = 90;
const FEEDBACK_EXCERPT_LIMIT = 160;
const FEEDBACK_EXAMPLE_LIMIT = 6;

const excerpt = (text: string, limit: number): string =>
    text.length > limit ? `${text.slice(0, limit)}…` : text;

const paragraphCount = (count: number): string => `${count} paragraph${count === 1 ? " " : "s "}`;

const DOCUMENT_UNCHANGED = "The document is unchanged and the assistant is redoing the change.";

type QuoteFirstParagraph = (texts: string[]) => string;

const quoteNothing: QuoteFirstParagraph = () => "";

const quoteFirst: QuoteFirstParagraph = (texts) =>
    `, starting with "${excerpt(texts[0], TOAST_EXCERPT_LIMIT)}"`;

const describeRefusal = (lost: string[], added: string[], quote: QuoteFirstParagraph): string => {
    if (lost.length > 0) {
        return `Not applied — this change would have removed ${paragraphCount(lost.length)}from the document ` +
            `without recording a tracked change${quote(lost)}. ${DOCUMENT_UNCHANGED}`;
    }
    if (added.length > 0) {
        return `Not applied — this change would have added ${paragraphCount(added.length)}` +
            `without being marked as an insertion${quote(added)}. ${DOCUMENT_UNCHANGED}`;
    }
    return "Not applied — this change would have reordered the document's text without recording a tracked change. " +
        DOCUMENT_UNCHANGED;
};

export class UntrackedWriteRefused extends UserFacingError {
    lost: string[];
    added: string[];

    constructor(lost: string[], added: string[]) {
        super(describeRefusal(lost, added, quoteNothing));
        this.name = "UntrackedWriteRefused";
        Object.defineProperties(this, {
            lost: { value: lost, enumerable: false, writable: true },
            added: { value: added, enumerable: false, writable: true },
        });
    }

    get userMessage(): string {
        return describeRefusal(this.lost, this.added, quoteFirst);
    }
}

const quoteExamples = (texts: string[]): string =>
    texts
        .slice(0, FEEDBACK_EXAMPLE_LIMIT)
        .map((text) => `  - "${excerpt(text, FEEDBACK_EXCERPT_LIMIT)}"`)
        .join("\n");

export const buildRegenerationFeedback = (lost: string[], added: string[]): string => {
    const parts = [
        "Your previous version of this edit was rejected before it reached the document, because applying it " +
        "would have changed text that no tracked change accounted for.",
        "The section you are editing is replaced wholesale by your output, so your output must represent " +
        "every paragraph of that section. Reproduce unchanged paragraphs exactly as they are, express removals " +
        "as tracked deletions rather than leaving the paragraph out, and express new text as a tracked insertion.",
    ];

    if (lost.length > 0) {
        parts.push(
            `${paragraphCount(lost.length)}would have disappeared with no tracked deletion. ` +
            `Keep them, and strike out only what the instruction asks you to remove:\n${quoteExamples(lost)}`,
        );
    }

    if (added.length > 0) {
        parts.push(
            `${paragraphCount(added.length)}would have appeared without being marked as an insertion. ` +
            `Wrap new text as a tracked insertion:\n${quoteExamples(added)}`,
        );
    }

    return parts.join("\n\n");
};

export interface GuardUntrackedWriteArgs {
    currentOoxml: string;
    incomingOoxml: string;
    applyContext: RedlineApplyContext;
    bookmarks: string[];
    recorder: RedlineAttemptRecorder;
}

export const guardUntrackedWrite = ({
    currentOoxml,
    incomingOoxml,
    applyContext,
    bookmarks,
    recorder,
}: GuardUntrackedWriteArgs): void => {
    const verdict = compareOriginalView(currentOoxml, incomingOoxml);
    if (verdict.ok) return;

    console.warn(
        `[untracked-write-guard] ${verdict.detail}`,
        {
            shape: verdict.shape,
            lostParagraphs: verdict.lost.length,
            addedParagraphs: verdict.added.length,
            lostCharacters: verdict.lost.map((paragraph) => paragraph.length),
            addedCharacters: verdict.added.map((paragraph) => paragraph.length),
            bookmarks,
        },
    );

    recorder.recordFailureAndKeepItsSnapshotId({
        applyContext,
        failure: {
            stage: "untracked_write_detected",
            bookmarks,
            ooxml: { attemptedOoxml: incomingOoxml, currentRangeOoxml: currentOoxml },
            lostParagraphs: verdict.lost.length,
            addedParagraphs: verdict.added.length,
        },
        error: new Error(`untracked write detected (${verdict.shape}): ${verdict.detail}`),
    });
};
